import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import request from "../../../Helpers/API";
import PlantillaScreen from "../../helpers/PlantillaScreen";
import { LoaderScreen } from "../../components/LoaderScreen";
import {
  FINANCIERAS_DOCUMENTOS,
  TIPOS_PERSONA_DOCUMENTO,
  LINEAS_CREDITO_DOCUMENTO,
  etiquetaCatalogo,
} from "../../../Arrays/DocumentosCreditoArray";
import { ValDocumentos, DOCUMENTO_MAX_LENGTH, DIAS_VIGENCIA_MAX } from "./ValDocumentos";

// Modal de alta / edición de documentos requeridos por financiera.
// Rediseñado: 2026-09-16 — IA (Claude). Usa la convención ram-* (backdrop + PlantillaScreen).
// Actualizado: 2026-09-17 — IA (Claude): nueva bandera "Requerido por cobranza" (documento_cobranza).
const TITULO_SWAL = "DOCUMENTOS";
const MENSAJE_ERROR_GENERICO =
  "No fue posible completar la operación. Intente nuevamente o contacte al administrador.";

const FORM_VACIO = {
  iddocumento: -999,
  documento: "",
  tipopersona: "",
  linea_credito: "",
  opcional: false,
  documentacion: false,
  fase2: false,
  documento_cobranza: false,
  dias_vigencia: 0,
};

const OPCIONES_SI_NO = [
  { campo: "opcional", etiqueta: "Opcional", ayuda: "El cliente puede omitirlo" },
  { campo: "documentacion", etiqueta: "Documentación", ayuda: "Forma parte del expediente" },
  { campo: "fase2", etiqueta: "Fase 2", ayuda: "Se solicita en la segunda fase" },
  { campo: "documento_cobranza", etiqueta: "Cobranza", ayuda: "Lo requiere el área de cobranza" },
];

const mensajeDeError = (error) =>
  typeof error?.mensaje === "string" && error.mensaje.trim() ? error.mensaje : MENSAJE_ERROR_GENERICO;

// Escapa texto para mostrarlo dentro de html de SweetAlert.
const escaparHtml = (texto) =>
  String(texto).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Campo con ícono a la izquierda (convención .ram-field).
const CampoModal = ({ icono, etiqueta, htmlFor, children }) => (
  <div className="ram-field">
    <div className="ram-field__icon">{icono}</div>
    <div className="ram-field__content">
      <label className="mdoc-label" htmlFor={htmlFor}>{etiqueta}</label>
      {children}
    </div>
  </div>
);

// Selector Sí / No tipo "segmented".
const SelectorSiNo = ({ etiqueta, ayuda, valor, onCambiar }) => (
  <div className="mdoc-toggle">
    <div className="mdoc-toggle__texto">
      <span className="mdoc-toggle__etiqueta">{etiqueta}</span>
      <span className="mdoc-toggle__ayuda">{ayuda}</span>
    </div>
    <div className="mdoc-toggle__opciones" role="radiogroup" aria-label={etiqueta}>
      <button
        type="button"
        role="radio"
        aria-checked={valor === true}
        className={`mdoc-toggle__btn ${valor === true ? "activo-si" : ""}`}
        onClick={() => onCambiar(true)}
      >
        Sí
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={valor === false}
        className={`mdoc-toggle__btn ${valor === false ? "activo-no" : ""}`}
        onClick={() => onCambiar(false)}
      >
        No
      </button>
    </div>
  </div>
);

export const ModalDocumentos = ({ open, onClose, financiera, iddocumento, onGuardado }) => {
  const [form, setForm] = useState(FORM_VACIO);
  const [loader, setLoader] = useState({ mostrar: false, alerta: false, error: false, mensaje: "" });
  const [guardando, setGuardando] = useState(false);
  const esEdicion = Number(iddocumento) > 0;
  const mostrarLoader = (mostrar) => setLoader((prev) => ({ ...prev, mostrar }));

  useEffect(() => {
    if (!open) return;
    if (!esEdicion) {
      setForm(FORM_VACIO);
      return;
    }
    mostrarLoader(true);
    request
      .get(`Documentos/BuscarID?iddocumento=${encodeURIComponent(Number(iddocumento))}`)
      .then((result) => {
        setForm({
          iddocumento: result?.iddocumento ?? iddocumento,
          documento: result?.documento ?? "",
          tipopersona: result?.tipopersona ?? "",
          linea_credito: result?.linea_credito ?? "",
          opcional: Boolean(result?.opcional),
          documentacion: Boolean(result?.documentacion),
          fase2: Boolean(result?.fase2),
          documento_cobranza: Boolean(result?.documento_cobranza),
          dias_vigencia: result?.dias_vigencia ?? 0,
        });
      })
      .catch((error) => {
        Swal.fire(TITULO_SWAL, mensajeDeError(error), "error");
        onClose?.();
      })
      .finally(() => mostrarLoader(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, iddocumento]);

  if (!open) return null;

  const cambiar = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));
  const onChange = (campo) => (e) => cambiar(campo, e.target.value);

  const onGuardar = () => {
    const resultado = ValDocumentos({ ...form, jdf: financiera });
    if (!resultado.isValid) {
      Swal.fire({
        title: TITULO_SWAL,
        html: resultado.errores.map(escaparHtml).join("<br>"),
        icon: "warning",
      });
      return;
    }

    mostrarLoader(true);
    setGuardando(true);
    request
      .post("Documentos", resultado.post)
      .then(async () => {
        mostrarLoader(false);
        setGuardando(false);
        await Swal.fire(
          TITULO_SWAL,
          esEdicion ? "Documento actualizado con éxito." : "Documento agregado con éxito.",
          "success"
        );
        onGuardado?.();
        onClose?.();
      })
      .catch((error) => {
        mostrarLoader(false);
        setGuardando(false);
        Swal.fire(TITULO_SWAL, mensajeDeError(error), "error");
      });
  };

  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loader.mostrar) onClose?.();
  };

  const nombreFinanciera = etiquetaCatalogo(FINANCIERAS_DOCUMENTOS, financiera);

  return (
    <div className="ram-backdrop" onClick={onBackdropClick}>
      <PlantillaScreen
        titulo={
          <>
            {esEdicion ? "Editar documento" : "Agregar documento"}
            <span className="mdoc-financiera">{nombreFinanciera}</span>
          </>
        }
        className="ram-modal modal-documentos-credito"
      >
        <button className="ram-close" onClick={onClose} type="button" aria-label="Cerrar">
          <CloseIcon style={{ fontSize: 14 }} />
        </button>

        <LoaderScreen loader={loader} />

        <form
          className="ram-body"
          onSubmit={(e) => {
            e.preventDefault();
            onGuardar();
          }}
        >
          <CampoModal icono={<DescriptionOutlinedIcon />} etiqueta="Documento" htmlFor="mdoc-documento">
            <input
              id="mdoc-documento"
              className="mdoc-input"
              type="text"
              placeholder="Ej. COMPROBANTE DE DOMICILIO"
              maxLength={DOCUMENTO_MAX_LENGTH}
              autoComplete="off"
              value={form.documento}
              onChange={onChange("documento")}
            />
          </CampoModal>

          <div className="mdoc-grid">
            <CampoModal icono={<PersonOutlineIcon />} etiqueta="Tipo de persona" htmlFor="mdoc-tipopersona">
              <select
                id="mdoc-tipopersona"
                className="mdoc-input"
                value={form.tipopersona}
                onChange={onChange("tipopersona")}
              >
                <option value="" disabled>Selecciona...</option>
                {TIPOS_PERSONA_DOCUMENTO.map((op) => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>
            </CampoModal>

            <CampoModal icono={<AccountBalanceOutlinedIcon />} etiqueta="Línea de crédito" htmlFor="mdoc-linea">
              <select
                id="mdoc-linea"
                className="mdoc-input"
                value={form.linea_credito}
                onChange={onChange("linea_credito")}
              >
                <option value="" disabled>Selecciona...</option>
                {LINEAS_CREDITO_DOCUMENTO.map((op) => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>
            </CampoModal>
          </div>

          <CampoModal icono={<EventAvailableOutlinedIcon />} etiqueta="Días de vigencia" htmlFor="mdoc-dias">
            <input
              id="mdoc-dias"
              className="mdoc-input mdoc-input--corto"
              type="number"
              min={0}
              max={DIAS_VIGENCIA_MAX}
              step={1}
              value={form.dias_vigencia}
              onChange={onChange("dias_vigencia")}
            />
          </CampoModal>

          <div className="mdoc-toggles">
            {OPCIONES_SI_NO.map(({ campo, etiqueta, ayuda }) => (
              <SelectorSiNo
                key={campo}
                etiqueta={etiqueta}
                ayuda={ayuda}
                valor={form[campo]}
                onCambiar={(valor) => cambiar(campo, valor)}
              />
            ))}
          </div>

          {/* Submit oculto: permite guardar con Enter. */}
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>

        <div className="ram-footer">
          <button type="button" className="ram-btn ram-btn--ghost" onClick={onClose} disabled={loader.mostrar}>
            Cancelar
          </button>
          <button type="button" className="ram-btn ram-btn--primary" onClick={onGuardar} disabled={loader.mostrar}>
            {guardando ? <span className="ram-btn__spinner" /> : <SaveIcon />}
            {guardando ? "Guardando..." : esEdicion ? "Guardar cambios" : "Agregar documento"}
          </button>
        </div>
      </PlantillaScreen>
    </div>
  );
};
